import { z } from 'zod';
export const dateStringValidation = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Must be ISO date format YYYY-MM-DD')
    .refine((s) => {
    const [year, month, day] = s.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
}, { message: 'Invalid date' });
export const optionalDateString = z
    .string()
    .regex(/^(\d{4}-\d{2}-\d{2})?\s*$/, 'Must be ISO date format YYYY-MM-DD or empty')
    .optional()
    .transform((val, ctx) => {
    if (val === undefined || val.trim() === '')
        return undefined;
    const result = dateStringValidation.safeParse(val);
    if (!result.success) {
        result.error.issues.forEach((issue) => ctx.addIssue({ code: 'custom', message: issue.message, path: issue.path }));
        return z.NEVER;
    }
    return result.data;
});
export const dateStringSchema = dateStringValidation
    .transform((s) => s)
    .describe('A calendar date as YYYY-MM-DD, with no time and no timezone.');
export function dateString(value) {
    return dateStringSchema.parse(value);
}
export function dateStringParts(value) {
    const [year, month, day] = value.split('-').map(Number);
    return { year, month, day };
}
export function toDateString(value) {
    if (typeof value !== 'string' || value === '')
        return null;
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
        const direct = dateStringValidation.safeParse(value);
        return direct.success ? direct.data : null;
    }
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime()))
        return null;
    return formatDate(parsed);
}
export function parseDate(ds) {
    const [year, month, day] = ds.split('-').map(Number);
    return new Date(year, month - 1, day);
}
export function formatDate(d) {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}
export function addDaysToDate(ds, days) {
    const d = parseDate(ds);
    d.setDate(d.getDate() + days);
    return formatDate(d);
}
export function addMonthsToDate(ds, months) {
    const d = parseDate(ds);
    const day = d.getDate();
    d.setDate(1);
    d.setMonth(d.getMonth() + months);
    const lastDayOfMonth = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
    d.setDate(Math.min(day, lastDayOfMonth));
    return formatDate(d);
}
export function monthEndAfter(ds, months) {
    const d = parseDate(ds);
    return formatDate(new Date(d.getFullYear(), d.getMonth() + months + 1, 0));
}
export function compareDates(a, b) {
    return a.localeCompare(b);
}
export function daysBetween(from, to) {
    const fromDate = parseDate(from);
    const toDate = parseDate(to);
    const diffTime = toDate.getTime() - fromDate.getTime();
    return Math.floor(diffTime / (1000 * 60 * 60 * 24));
}
export function currentDateString(now = new Date()) {
    return dateString(now.toISOString().slice(0, 10));
}
export function isValidTimeZone(timeZone) {
    if (timeZone === '')
        return false;
    try {
        new Intl.DateTimeFormat('en-US', { timeZone });
        return true;
    }
    catch {
        return false;
    }
}
export function civilDateInTimeZone(instant, timeZone) {
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).formatToParts(instant);
    const part = (type) => parts.find((p) => p.type === type)?.value;
    return dateString(`${part('year')}-${part('month')}-${part('day')}`);
}
//# sourceMappingURL=dateString.js.map